import React, { useState, useEffect } from 'react';
import { 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  Grid,
  Snackbar
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { 
  useAuthProfileRetrieveQuery,
  useAppointmentsCreateMutation,
  useAppointmentsListQuery
} from '../../../services/GLOWClient';
import AppointmentCalendar from './AppointmentCalendar';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useSalonPublicTheme } from '../../../context/SalonPublicThemeContext';
import { useAuth } from '../../../context/AuthContext';
import Button from '../../common/salonPublicAppearance/SalonPublicButton';
import IconButton from '../../common/salonPublicAppearance/SalonPublicIconButton';

const AppointmentBookingDialog = ({ 
  open, 
  onClose, 
  onSuccess, 
  salonId, 
  serviceId, 
  serviceName, 
  appointmentDetails: preSelectedAppointment,
  onAuthRequired // Add this prop to handle authentication
}) => {
  const { tokens: theme } = useSalonPublicTheme();
  // State for the stepper
  const [activeStep, setActiveStep] = useState(0);
  const [appointmentDetails, setAppointmentDetails] = useState(null);
  const [conflictError, setConflictError] = useState(null);
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [conflictType, setConflictType] = useState(null); // 'same_slot' or 'double_booking'
  
  // Get authentication status
  const { isAuthenticated } = useAuth();
  
  // Get current user profile and authentication status
  const { data: userProfile, isError: profileError } = useAuthProfileRetrieveQuery(undefined, {
    skip: !open || !isAuthenticated() // Only run the query when the dialog is open AND user is authenticated
  });

  // Fetch user's existing appointments to check for double-booking
  const { data: existingAppointments, refetch: refetchExistingAppointments } = useAppointmentsListQuery(
    { client: userProfile?.id },
    { skip: !userProfile?.id }
  );

  // Check if user is authenticated
  const userIsAuthenticated = isAuthenticated() && !profileError && userProfile?.id;

  // Handle dialog open/close based on authentication
  useEffect(() => {
    if (open && !isAuthenticated) {
      onAuthRequired?.();
      onClose();
    }
  }, [open, isAuthenticated, onAuthRequired, onClose]);
  
  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setActiveStep(preSelectedAppointment ? 1 : 0);
      
      // Ensure we have all required fields in the appointment details
      const details = preSelectedAppointment ? {
        ...preSelectedAppointment,
        salon: preSelectedAppointment.salon || preSelectedAppointment.salon_id,
        service: preSelectedAppointment.service || preSelectedAppointment.service_id,
        teamMember: preSelectedAppointment.teamMember || preSelectedAppointment.teamMemberId,
        date: preSelectedAppointment.date || new Date().toISOString().split('T')[0],
        time: preSelectedAppointment.time || preSelectedAppointment.selectedTime,
        duration: preSelectedAppointment.duration || 60
      } : null;
      
      setAppointmentDetails(details);
    }
  }, [open, preSelectedAppointment]);
  
  // Mutation for creating appointment
  const [createAppointment, { isLoading: isCreating, error: createError }] = useAppointmentsCreateMutation();

  const getCreateApiErrorMessage = (error) => {
    if (!error) return "Une erreur s'est produite lors de la création du rendez-vous.";
    const data = error.data || error.error || error.message;
    if (typeof data === 'string') return data;
    if (data && typeof data === 'object') {
      if (Array.isArray(data.start_time) && data.start_time.length > 0) {
        return data.start_time.join('\n');
      }
      if (typeof data.error === 'string') return data.error;
    }
    return "Une erreur s'est produite lors de la création du rendez-vous.";
  };
  
  // Handle slot selection
  const handleSlotSelect = (details) => {
    setAppointmentDetails({
      ...details,
      // Ensure we have the service name from either the details or the prop
      service_name: details.service_name || serviceName,
      // Ensure we have the service ID
      service: details.service || details.service_id,
      // Ensure we have the salon ID
      salon: details.salon || salonId
    });
    setActiveStep(1);
  };
  
  // Handle back button click
  const handleBack = () => {
    setActiveStep(0);
  };

  // Vérifier les conflits de réservation
  const checkConflicts = async () => {
    if (!appointmentDetails || !userProfile) return true;

    try {
      // Refetch les RDV existants pour avoir les données à jour
      const result = await refetchExistingAppointments();
      const appointments = result.data || [];

      const startDateTime = new Date(`${appointmentDetails.date}T${appointmentDetails.time}:00`);
      const serviceDuration = appointmentDetails.duration || 60;
      const endDateTime = new Date(startDateTime.getTime() + serviceDuration * 60000);

      // Vérifier les conflits avec les RDV existants
      if (appointments && Array.isArray(appointments)) {
        for (const existing of appointments) {
          const existingStart = new Date(existing.start_time);
          const existingEnd = new Date(existing.end_time);

          // Vérifier le chevauchement
          if (startDateTime < existingEnd && endDateTime > existingStart) {
            setConflictError(
              `Vous avez déjà un rendez-vous à cette heure dans le salon "${existing.salon_name || 'ce salon'}"`
            );
            setConflictType('double_booking');
            setShowConflictDialog(true);
            return false;
          }
        }
      }

      return true;
    } catch (error) {
      console.error('Erreur lors de la vérification des conflits:', error);
      return true; // Permettre la création si la vérification échoue
    }
  };
  
  // Handle confirm button click
  const handleConfirm = async () => {
 
    if (!appointmentDetails || !userProfile) {
      console.error('Missing appointment details or user profile');
      return;
    }

    // Vérifier les conflits avant de créer le RDV
    const noConflict = await checkConflicts();
    if (!noConflict) {
      return;
    }
    
    try {
      console.log('Creating appointment with details:', {
        appointmentDetails,
        salonId,
        userProfileId: userProfile?.id
      }
    );

      // Combine date and time to create start_time
      const dateStr = appointmentDetails.date; // Format: YYYY-MM-DD
      const timeStr = appointmentDetails.time; // Format: HH:MM
      const startDateTime = new Date(`${dateStr}T${timeStr}:00`);
      
      // Calculate end_time by adding service duration
      const serviceDuration = appointmentDetails.duration || 60; // Default to 60 minutes if not available
      const endDateTime = new Date(startDateTime.getTime() + serviceDuration * 60000);
      
      // Format dates in local time for the API
      const formatLocalISODateTime = (date) => {
        // Convert to local time string and back to Date to handle timezone correctly
        const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
        return localDate.toISOString().slice(0, -1); // Remove 'Z' to indicate local time
      };
      
      const start_time = formatLocalISODateTime(startDateTime);
      const end_time = formatLocalISODateTime(endDateTime);
      
      // Get salon ID from multiple possible sources, prioritizing service.salon
      const resolvedSalonId = Number(
        appointmentDetails?.salon_details?.salon_id || // From service.salon_details
        appointmentDetails?.salon_id ||              // From service.salon_id
        appointmentDetails?.salon ||                 // From direct salon prop
        salonId                                      // From component prop
      );


      if (!resolvedSalonId) {
        throw new Error('Could not determine salon ID for the appointment');
      }

      const rawTeamMember =
        appointmentDetails.teamMemberId ?? appointmentDetails.teamMember;

      // Prepare appointment data according to API requirements
      const appointmentData = {
        salon: resolvedSalonId,
        service: Number(appointmentDetails.service_id || appointmentDetails.service),
        team_member: rawTeamMember ? Number(rawTeamMember) : null,
        client: Number(userProfile?.id),
        start_time: start_time,
        end_time: end_time
      };
 
      const result = await createAppointment({
        appointmentCreateRequest: appointmentData
      }).unwrap();
      
     
      // Call parent success handler first
      if (onSuccess) {
        onSuccess(result);
      }
      // Close dialog immediately to show parent snackbar
      onClose();
    } 
    catch (error) {
      console.error('Failed to create appointment:', error);
    }
  };
  
  // Steps for the stepper
  const steps = [
    'Sélectionner un créneau',
    'Confirmer le rendez-vous'
  ];
  
  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: theme.borderRadius.lg,
            overflow: 'hidden'
          }
        }}
      >
      <DialogTitle
        style={{
          backgroundColor: theme.colors.primary[50],
          fontFamily: theme.typography.fontFamily.sans.join(', '),
        }}
        sx={{
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: theme.colors.primary[50],
        borderBottom: `1px solid ${theme.colors.grey[200]}`,
        fontWeight: theme.typography.fontWeight.bold,
        fontFamily: theme.typography.fontFamily.sans.join(', '),
        fontSize: '1.5rem'
        }}
      >
        Prendre rendez-vous
        <IconButton aria-label="Fermer" onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3, pt: 4 }}>
        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {/* Step content */}
        {activeStep === 0 ? (
          <Box>
            <Typography variant="h6" gutterBottom>
              {serviceName}
            </Typography>
            <AppointmentCalendar 
              salonId={salonId} 
              serviceId={serviceId}
              onSelectSlot={handleSlotSelect}
            />
          </Box>
        ) : (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: theme.typography.fontWeight.bold, mb: 2 }}>
              Confirmation du rendez-vous
            </Typography>
            
           
            
            {createError && (
              <Alert severity="error" sx={{ mb: 3, whiteSpace: 'pre-line' }}>
                {createError?.data?.debug_info?.errors ? (
                  <>
                    <Typography variant="subtitle2" fontWeight="bold">Erreurs:</Typography>
                    <ul style={{ marginTop: '4px', paddingLeft: '20px' }}>
                      {Array.isArray(createError.data.debug_info.errors) ? 
                        createError.data.debug_info.errors.map((err, idx) => (
                          <li key={idx}>{err}</li>
                        )) : 
                        <li>{createError.data.debug_info.errors}</li>
                      }
                    </ul>
                  </>
                ) : (
                  getCreateApiErrorMessage(createError)
                )}
              </Alert>
            )}
            
            <Box sx={{ 
              p: 3, 
              borderRadius: theme.borderRadius.md, 
              bgcolor: theme.colors.primary[50],
              mb: 3
            }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Service:</strong> {appointmentDetails?.service_name || serviceName || 'Non spécifié'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Date:</strong> {appointmentDetails?.date ? format(new Date(appointmentDetails.date), 'EEEE d MMMM yyyy', { locale: fr }) : ''}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Heure:</strong> {appointmentDetails?.time || 'Non spécifiée'}
              </Typography>
              {appointmentDetails?.teamMemberDetails?.user_details && (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Professionnel:</strong> {appointmentDetails.teamMemberDetails.user_details.full_name || 'Non spécifié'}
                </Typography>
              )}
              {appointmentDetails?.duration && (
                <Typography variant="body1">
                  <strong>Durée:</strong> {appointmentDetails.duration} minutes
                </Typography>
              )}
            </Box>
            
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ 
        p: 2, 
        borderTop: `1px solid ${theme.colors.grey[200]}`,
        bgcolor: theme.colors.grey[50]
      }}>
        {activeStep === 0 ? (
          <Button onClick={onClose} color="inherit">
            Annuler
          </Button>
        ) : (
          <>
            <Button onClick={handleBack} color="inherit" disabled={isCreating}>
              Retour
            </Button>
            <Button 
              onClick={() => {
                handleConfirm();
              }} 
              variant="contained" 
              color="primary"
              disabled={isCreating}
              startIcon={isCreating ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isCreating ? 'Création...' : 'Confirmer le rendez-vous'}
            </Button>
          </>
        )}
      </DialogActions>
      
      </Dialog>

      {/* Dialog pour afficher les conflits de réservation */}
      <Dialog 
        open={showConflictDialog} 
        onClose={() => setShowConflictDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          bgcolor: theme.colors.error[50],
          borderBottom: `2px solid ${theme.colors.error[200]}`,
          fontWeight: 'bold'
        }}>
          ⚠️ Conflit de réservation
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {conflictError}
          </Alert>
          
          {conflictType === 'double_booking' && (
            <Box>
              <Typography variant="body2" sx={{ mb: 2, color: theme.colors.text.secondary }}>
                Vous avez déjà un rendez-vous à cette heure. Veuillez choisir un autre créneau horaire.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setShowConflictDialog(false);
              setConflictError(null);
              setConflictType(null);
            }}
            variant="contained"
            color="primary"
          >
            Choisir un autre créneau
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AppointmentBookingDialog;
